import React, { useState, useEffect, useContext } from "react";
import PropTypes from "prop-types";
import { useMsal } from "@azure/msal-react";
import { Box, CircularProgress, Typography, Divider, FormControlLabel } from "@mui/material";
import StyledModal from "../../Modals/StyledModal/StyledModal";
import PrimaryButton from "../../Buttons/PrimaryButton/PrimaryButton";
import SecondaryButton from "../../Buttons/SecondaryButton/SecondaryButton";
import PrimaryTextField from "../../Inputs/PrimaryTextField/PrimaryTextField";
import PrimarySelect from "../../Inputs/PrimarySelect/PrimarySelect";
import PrimaryCheckbox from "../../Inputs/PrimaryCheckBox/PrimaryCheckBox";
import RateConfirmationModalInfo from "./RateConfirmationModalInfo/RateConfirmationModalInfo";
import RateConfirmationModalSidebar from "./RateConfirmationModalSidebar/RateConfirmationModalSidebar";
import { MovementService } from "../../../services/MovementService";
import {
  findRateConfirmationContact,
  findRateConfirmationContactById,
  getRateConfirmationContactOptions
} from "../../../utils/rateConfirmationContactHelpers";
import { AlertContext, ActiveUserContext } from "../../Dashboard/Dashboard";
import { handleAltEnter } from "../../../utils/keyboardShortcutHelpers";

export default function RateConfirmationModal({
  open,
  handleClose,
  rateConData,
  load,
  carrier,
  carrierContacts
}) {
  const { instance } = useMsal();
  const { warning } = useContext(AlertContext);
  const [isLoading, setIsLoading] = useState(false);
  const [submitDisabled, setSubmitDisabled] = useState(true);
  const [comments, setComments] = useState(rateConData.comments);
  const [template, setTemplate] = useState(22016);
  const [templates, setTemplates] = useState([]);
  const [contactsSelected, setContactsSelected] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [emailChecked, setEmailChecked] = useState(true);
  const activeUser = useContext(ActiveUserContext);

  const handleCancel = () => {
    handleClose();
  };

  const handlePreview = () => {
    const contact = findRateConfirmationContactById(contacts, contactsSelected[0])
      || findRateConfirmationContact(contacts, rateConData);

    setIsLoading(true);
    const payload = {
      movementId: rateConData.movementId,
      templateNumber: template.toString(),
      print: true,
      contactEmail: contact?.email || rateConData.contactEmail,
      contactName: contact?.name || rateConData.carrierContact,
      contactId: contact?.id || rateConData.contactId
    };

    MovementService.SendConfirmation(instance, payload).then(res => {
      res.arrayBuffer().then(data => {
        const file = new Blob([data], { type: "application/pdf" });
        window.open(URL.createObjectURL(file));
      });
      setIsLoading(false);
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      warning(error);
    });
  };

  const handleContactChange = value => {
    setContactsSelected(value);
    if (value.length > 0) {
      setSubmitDisabled(false);
    }
    else {
      setSubmitDisabled(true);
    }
  };

  async function handleSave() {
    setIsLoading(true);
    setSubmitDisabled(true);
    let submitAllSuccess = false;

    const payload = {
      orderId: rateConData.orderId,
      movementId: rateConData.movementId,
      templateNumber: template.toString(),
      print: false,
      fromEmail: activeUser.email,
      fromName: activeUser.userName,
      additionalComments: comments
    };

    for (let contactId of contactsSelected) {
      let contact = findRateConfirmationContactById(contacts, contactId);
      payload.contactEmail = contact.email;
      payload.contactName = contact.name;
      payload.contactId = contact.id;

      await MovementService.SendConfirmationEmail(instance, payload).then(() => {
        submitAllSuccess = true;
      }).catch(error => {
        console.error(error);
        submitAllSuccess = false;
        warning(error);
      });
    }

    if (submitAllSuccess) {
      alert("Confirmation sent successfully");
      setIsLoading(false);
      handleClose();
    }
  }

  useEffect(() => {
    if (open) {
      const contactOptions = getRateConfirmationContactOptions(carrierContacts);
      const selectedContact = findRateConfirmationContact(contactOptions, rateConData);

      setContacts(contactOptions);
      setContactsSelected(selectedContact ? [selectedContact.id] : []);
      setSubmitDisabled(!selectedContact);
    }
  }, [carrierContacts, open, rateConData]);

  useEffect(() => {
    setIsLoading(true);
    MovementService.GetConfirmationTemplates(instance).then(res => {
      setTemplates(res);
      setIsLoading(false);
    }).catch(error => {
      console.error(error);
      setIsLoading(false);
      warning(error);
    });
  }, []);

  return (
    <StyledModal
      label={"Rate Confirmation"}
      title={"Rate Confirmation"}
      open={open}
      handleClose={handleClose}
    >
      <Box onClick={(e) => handleAltEnter(e, (submitDisabled || !emailChecked), handleSave)}>
        {isLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", marginTop: "20px" }}>
            <CircularProgress sx={{ color: "primary.60" }} size={40} />
          </Box>
        ) : (
          <Box sx={{ maxHeight: "70vh", maxWidth: "85vw", overflow: "scroll", display: "flex" }}>
            <Box>
              <RateConfirmationModalInfo rateConData={rateConData} load={load} carrier={carrier} />
              <Box sx={{ marginY: "20px" }}>
                <Typography variant="subhead2" color="primary.60">Add Ons</Typography>
                <Divider />
              </Box>
              <PrimaryTextField
                id="rate-confirmation-modal-additional-comments"
                label="Additional Comments"
                onChange={setComments}
                value={comments}
                width="500px"
              />
              <Box sx={{ display: "flex", marginY: "10px" }}>
                <Box sx={{ display: "flex", flexDirection: "column", alignItems: "end" }}>
                  <Typography
                    sx={{
                      color: "primary.60",
                      cursor: "pointer"
                    }}
                    onClick={handlePreview}>
                    Preview
                  </Typography>
                  <PrimarySelect
                    label="Choose a template"
                    options={templates.map((o) => (o))}
                    onChange={setTemplate}
                    value={template}
                    flexGrow={1}
                    nameKey="descr"
                    valueKey="template_number"
                    disabled
                  />
                </Box>
              </Box>
              <Box sx={{ marginY: "20px" }}>
                <Typography variant="subhead2" color="primary.60">Recipients</Typography>
                <Divider />
              </Box>
              <Box>
                <Typography variant="subhead2" marginY="10px">{rateConData.carrierName}</Typography>
                <Box display="flex" marginY="10px" maxWidth="500px" sx={{ alignItems: "center" }}>
                  <PrimarySelect
                    label="Recipients"
                    options={contacts}
                    onChange={o => handleContactChange(o)}
                    value={contactsSelected}
                    flexGrow={1}
                    nameKey="recipientLabel"
                    valueKey="id"
                    disabled={isLoading || !emailChecked}
                    multiple
                  />
                  <FormControlLabel sx={{ marginLeft: "30px" }}
                    control={
                      <Box sx={{ marginRight: "8px" }}>
                        <PrimaryCheckbox
                          checked={emailChecked}
                          handleChange={e => setEmailChecked(e.target.checked)}
                          color="black_solid.60"
                          disabled={isLoading}
                        />
                      </Box>
                    }
                    label={
                      <Typography color="black_solid.80">Email</Typography>
                    }
                  />
                </Box>
              </Box>
            </Box>
            <RateConfirmationModalSidebar load={load} />
          </Box>
        )}
        <Box sx={{ display: "flex", marginTop: "20px" }}>
          <Box sx={{ marginLeft: "auto", padding: "5px 10px 5px 5px" }}>
            <SecondaryButton
              label="Cancel"
              onClick={handleCancel}
              disabled={isLoading}
            />
          </Box>
          <Box sx={{ padding: "5px 10px 5px 5px" }}>
            <SecondaryButton
              label="Preview Attachment"
              onClick={handlePreview}
              disabled={isLoading}
            />
          </Box>
          <Box sx={{ padding: "5px 0 5px 5px" }}>
            <PrimaryButton
              label="Create Email"
              width="100px"
              onClick={handleSave}
              isLoading={isLoading}
              disabled={submitDisabled || !emailChecked}
            />
          </Box>
        </Box>
      </Box>
    </StyledModal>
  );
}

RateConfirmationModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClose: PropTypes.func.isRequired,
  rateConData: PropTypes.object.isRequired,
  load: PropTypes.object,
  carrier: PropTypes.object,
  carrierContacts: PropTypes.array
};
